import React from 'react'
import {
  Dimensions,
  Image,
  ImageStyle,
  Platform,
  TextStyle,
  View,
  ViewStyle,
  TouchableWithoutFeedback,
} from 'react-native'
import { observer } from 'mobx-react-lite'
import { color } from '../../theme'
import { Skeleton } from '../skeleton'
import { Text } from '../text'
import { getStrongestWeakest, QuizPctCorrectSummary } from '@trivie/core'
import { Instance } from 'mobx-state-tree'
import LinearGradient from 'react-native-linear-gradient'
import { useTranslation } from 'react-i18next'
import { Card } from '../card'
import { AssignedTopic } from '@trivie/core/src/models/AssignedTopic'
import defaultCover from './default-topic-image.png'

interface QuizStatsProps {
  stats: Array<Instance<typeof QuizPctCorrectSummary>> | null
  style?: ViewStyle
  loading?: boolean
  quizzes?: Array<Instance<typeof AssignedTopic>> | []
  onPress: (item: Instance<typeof QuizPctCorrectSummary>) => void
}

export const QuizStats = observer((props: QuizStatsProps) => {
  const { loading, stats, style, onPress } = props

  const { strongest, weakest } = getStrongestWeakest(stats ?? [])

  if (loading) {
    return <Skeleton style={LOADER} />
  }

  return (
    <>
      {weakest?.length > 0 && (
        <Card
          style={{
            ...CONTAINER,
            ...style,
            alignItems: 'flex-start',
            marginRight: Platform.OS === 'web' ? 18 : 0,
          }}
        >
          {weakest.map((quiz: Instance<typeof QuizPctCorrectSummary>) => (
            <QuizItem quiz={quiz} onPress={() => onPress(quiz)} />
          ))}
        </Card>
      )}

      {strongest?.length > 0 && (
        <Card style={{ ...CONTAINER, ...style, alignItems: 'flex-start' }}>
          {strongest.map((quiz: Instance<typeof QuizPctCorrectSummary>) => (
            <QuizItem strongest quiz={quiz} onPress={() => onPress(quiz)} />
          ))}
        </Card>
      )}
    </>
  )
})

interface QuizItemProps {
  quiz: Instance<typeof QuizPctCorrectSummary>
  strongest?: boolean
  onPress?: () => void
}
const QuizItem = observer((props: QuizItemProps) => {
  const { quiz, strongest, onPress } = props

  const photoUrl = defaultCover
  let imageSource = {
    uri: photoUrl || defaultCover,
    cache: 'force-cache',
  }
  if (Platform.OS === 'ios') {
    imageSource = require('./default-topic-image.png')
  }

  return (
    <TouchableWithoutFeedback disabled style={{ width: '100%' }} onPress={onPress}>
      <View
        style={{
          flexDirection: 'row',
          width: '100%',
          marginBottom: 25,
          //@ts-ignore
          cursor: 'pointer',
        }}
      >
        <View style={{ borderRadius: 6, alignSelf: 'center', width: 40 }}>
          <Image style={TOPIC_IMG} resizeMode="cover" source={imageSource as any} />
        </View>
        <View style={TOPIC_CONTAINER}>
          <Text
            numberOfLines={1}
            style={{ marginBottom: 5 }}
            text={quiz.quiz_title}
            variant="body1Emphasized"
          />
          <ProgressGradient strongest={strongest} value={quiz.pct_correct} />
        </View>
      </View>
    </TouchableWithoutFeedback>
  )
})

interface ProgressProps {
  strongest: boolean | undefined
  value: number
}
const ProgressGradient = observer((props: ProgressProps) => {
  const { strongest, value } = props

  const { t } = useTranslation()
  return (
    <View style={PROGRESS_ROOT}>
      <View style={PROGRESS_CONTAINER}>
        <View style={{ ...PROGRESS_METER, backgroundColor: strongest ? '#1FE08F20' : '#FF3E1320' }}>
          <LinearGradient
            useAngle
            angle={143.13}
            locations={[0, 1]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            colors={strongest ? ['#2FEA9B', '#7FDD53'] : ['#FFBF1A', '#FF4080']}
            style={{ ...GRADIENT, width: `${value}%` }}
          />
        </View>
        <Text
          style={PCT_TEXT}
          variant="body2Emphasized"
          tx={`${Math.round(value).toFixed(0)}% ${t('Common.correct')}`}
        />
      </View>
    </View>
  )
})

const PCT_TEXT: TextStyle = { bottom: 3, paddingLeft: 9, opacity: 0.75 }
const GRADIENT: ViewStyle = { borderRadius: 100, height: 10 }
const TOPIC_CONTAINER: ViewStyle = { flexDirection: 'column', paddingLeft: 12, flex: 1 }
const PROGRESS_ROOT: ViewStyle = { width: '100%', flex: 1 }
const PROGRESS_CONTAINER: ViewStyle = { ...PROGRESS_ROOT, flexDirection: 'row' }
const PROGRESS_METER: ViewStyle = {
  height: 10,
  borderRadius: 100,
  width: '100%',
  flex: 1,
  flexDirection: 'row',
}
const CONTAINER: ViewStyle = {
  borderWidth: 2,
  borderColor: '#F3F2F1',
  backgroundColor: color.white,
  borderRadius: 20,
  padding: 20,
  marginBottom: 16,
  width: Platform.OS === 'web' ? '100%' : Dimensions.get('screen').width - 40,
  paddingBottom: 0,
}
const LOADER: ViewStyle = {
  height: 435,
  width: Platform.OS === 'web' ? '100%' : Dimensions.get('screen').width - 40,
  borderRadius: 20,
  marginBottom: 16,
}
const TOPIC_IMG: ImageStyle = {
  borderRadius: 6,
  width: 40,
  height: 32,
  resizeMode: 'cover',
}
